import { Component, DestroyRef, HostBinding, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Evaluation } from '@app/models/enseignant';
import { PermissionService, PlatformService, StorageService, UserService } from '@app/services';
import { EnseignantService } from '@app/services/enseignant.service';
import { distinctUntilChanged, startWith } from 'rxjs';

@Component({
  selector: 'app-list-evaluation',
  templateUrl: './list-evaluation.component.html',
  styleUrl: './list-evaluation.component.scss',
  standalone: false
})
export class ListEvaluationComponent implements OnInit {

  @HostBinding('class.height-full-minus-header-subheader') hostClass = false;
  @HostBinding('class.height-full-minus-header') hostClassForMobile = false;

  enseignantService = inject(EnseignantService);
  userService = inject(UserService);
  platformService = inject(PlatformService);
  storageService = inject(StorageService);
  destroyRef = inject(DestroyRef);
  permService = inject(PermissionService);

  evaluationsList: Evaluation[];        // Toutes les données
  filteredEvaluations: Evaluation[] = [];    // Données après filtre

  // Filtres actifs
  libelleFilter = '';
  numFilter = '';
  coursFilter = '';
  disciplineFilter = '';
  startDate: Date | null = null;
  endDate: Date | null = null;
  eleveFilter: string[] = [];

  // Options de filtre
  eleveOptions: { key: string; label: string }[] = [];

  // Pagination
  pageSize = 12;
  totalItems = 0;
  pageIndex = 0;
  pageSizeOptions = [12, 24, 48];

  idEnseignant: number;
  filterSettings = { opened: false, mode: 'over', width: 150 };
  isMobile: boolean = false;
  tableView: boolean = false; // false = cartes, true = tableau
  // permToCreate: boolean = false;

  private readonly STORAGE_KEY = 'evaluations-filters';

  ngOnInit(): void {
    this.idEnseignant = this.userService.currentUser.idEnseignant;
    // this.permToCreate = this.permService.hasPermission('enseignant_evaluation_create');

    this.loadFiltersFromStorage();

    this.enseignantService.getAllEvaluationByIdEnseignant(this.idEnseignant)
      .subscribe(evaluations => {
        this.evaluationsList = evaluations;
        const eleveSeen = new Map<string, { key: string; label: string }>();
        evaluations
          .flatMap(e => e.allInscriptions ?? [])
          .forEach(i => {
            const key = i.idEnfant ? `e_${i.idEnfant}` : `a_${i.idAdulte}`;
            if (!eleveSeen.has(key))
              eleveSeen.set(key, { key, label: i.nomReferent ?? key });
          });
        this.eleveOptions = [...eleveSeen.values()].sort((a, b) => a.label.localeCompare(b.label));

        this.applyFilters();
      });

    this.platformService.isMobile$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged())
      .subscribe((isMobile) => {
        this.hostClassForMobile = isMobile;
        this.hostClass = !isMobile;
        this.isMobile = isMobile;
        this.tableView = false;
      });

    this.platformService.mainWidth$
      .pipe(
        startWith(this.platformService.mainWidth()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(mainWidth => {
        const isOver = mainWidth < 1200;
        this.filterSettings.mode = isOver ? 'over' : 'side';
      });
  }

  deleteEvaluation(id: number) {
    this.evaluationsList = this.evaluationsList.filter(e => e.idEvaluation !== id);
    this.filteredEvaluations = this.filteredEvaluations.filter(e => e.idEvaluation !== id);
    this.totalItems = this.filteredEvaluations.length;
  }

  onLibelleFilterChange(event: any): void {
    this.libelleFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onNumFilterChange(event: any): void {
    this.numFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onCoursFilterChange(event: any): void {
    this.coursFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onDisciplineFilterChange(event: any): void {
    this.disciplineFilter = event.target.value?.toLowerCase() || '';
    this.applyFilters();
  }

  onDateRangeFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredEvaluations = this.evaluationsList.filter(data => {
      const libelleMatch = !this.libelleFilter || data.libelle?.toLowerCase().includes(this.libelleFilter.toLowerCase());
      const numMatch = !this.numFilter || data.numEvaluation?.toString().toLowerCase().includes(this.numFilter.toLowerCase());
      const coursMatch = !this.coursFilter || data?.cours?.libelle?.toLowerCase().includes(this.coursFilter.toLowerCase());
      const disciplineMatch = !this.disciplineFilter || data?.discipline?.libelle?.toLowerCase().includes(this.disciplineFilter.toLowerCase());

      const dateDebut = new Date(data.dateDebut);
      const startDateMatch = !this.startDate || dateDebut >= this.startDate;
      const endDateMatch = !this.endDate || dateDebut <= this.endDate;

      const eleveMatch = this.eleveFilter.length === 0 ||
        this.eleveFilter.some(key => data.allInscriptions?.some(i => (i.idEnfant ? `e_${i.idEnfant}` : `a_${i.idAdulte}`) === key));

      return libelleMatch && numMatch && coursMatch && disciplineMatch && startDateMatch && endDateMatch && eleveMatch;
    });

    this.totalItems = this.filteredEvaluations.length;
    this.pageIndex = 0; // retour à la première page
    this.saveFiltersToStorage();
  }

  private loadFiltersFromStorage(): void {
    const filters = this.storageService.getItem(this.STORAGE_KEY);
    if (!filters) return;
    this.libelleFilter = filters.libelleFilter || '';
    this.numFilter = filters.numFilter || '';
    this.coursFilter = filters.coursFilter || '';
    this.disciplineFilter = filters.disciplineFilter || '';
    this.startDate = filters.startDate ? new Date(filters.startDate) : null;
    this.endDate = filters.endDate ? new Date(filters.endDate) : null;
    this.eleveFilter = filters.eleveFilter || [];
  }

  private saveFiltersToStorage(): void {
    this.storageService.setItem(this.STORAGE_KEY, {
      libelleFilter: this.libelleFilter,
      numFilter: this.numFilter,
      coursFilter: this.coursFilter,
      disciplineFilter: this.disciplineFilter,
      startDate: this.startDate,
      endDate: this.endDate,
      eleveFilter: this.eleveFilter,
    });
  }

  hasActiveFilters(): boolean {
    return !!(this.libelleFilter || this.numFilter || this.coursFilter || this.disciplineFilter || this.startDate || this.endDate || this.eleveFilter.length);
  }

  resetFilters(): void {
    this.libelleFilter = '';
    this.numFilter = '';
    this.coursFilter = '';
    this.disciplineFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.eleveFilter = [];
    this.storageService.removeItem(this.STORAGE_KEY);
    this.applyFilters();
  }

  pageChange(data: PageEvent) {
    this.pageSize = data.pageSize;
    this.pageIndex = data.pageIndex;
  }

  closeDrawer() {
    this.filterSettings.opened = false;
  }

  toggleOptions() {
    this.filterSettings.opened = !this.filterSettings.opened;
  }
}
